import React, { useState } from 'react';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/register-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          project_type: formData.projectType,
          message: formData.message,
        }),
      });
      if (!response.ok) throw new Error('Submission failed');
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
      }, 4000);
    } catch (err) {
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="register" className="relative py-32 px-6 md:px-[7.6923%]">
      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 7.6923%), repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 7.6923%)',
          backgroundSize: '100% 100%',
          opacity: 0.03,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <div className="space-y-8">
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-semibold"
              style={{
                color: 'var(--text-primary)',
                letterSpacing: '-0.02em',
              }}
            >
              Ready to Transform Your Space?
            </h2>
            <p
              className="text-lg md:text-xl"
              style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}
            >
              While we're building our AR platform, we're offering traditional interior design consultancy services. Register your interest and our team will reach out to discuss your project.
            </p>

            {/* Benefits */}
            <div className="space-y-4">
              {[
                'Professional design consultation',
                'Personalized space planning',
                'Material and furniture recommendations',
                'Early access to AR platform when launched',
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 size={24} color="#FF8000" strokeWidth={2} />
                  <span className="text-base" style={{ color: 'var(--text-secondary)' }}>
                    {benefit}
                  </span>
                </div>
              ))}
            </div>

            {/* Contact info */}
            <div
              className="pt-8 border-t space-y-2"
              style={{ borderColor: 'rgba(255, 255, 255, 0.25)' }}
            >
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Or reach us directly:
              </p>
              <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
                <strong>Tel:</strong> +6016-331 8826
              </p>
              <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
                <strong>Email:</strong> enquiry@immersid.com
              </p>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="glass p-8 md:p-10 rounded-3xl border-2 border-orange-200">
            {submitted ? (
              <div className="text-center py-12">
                <CheckCircle2 size={64} color="#FF8000" className="mx-auto mb-6" />
                <h3
                  className="text-2xl font-semibold mb-4"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Thank You!
                </h3>
                <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
                  We've received your interest. Our team will contact you within 24 hours to discuss your project.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#CCCCCC] text-[#1A1A1A] focus:outline-none focus:border-[#FF8000] transition-colors duration-300 rounded-lg"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#CCCCCC] text-[#1A1A1A] focus:outline-none focus:border-[#FF8000] transition-colors duration-300 rounded-lg"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#CCCCCC] text-[#1A1A1A] focus:outline-none focus:border-[#FF8000] transition-colors duration-300 rounded-lg"
                    placeholder="+60 12-345-6789"
                  />
                </div>

                <div>
                  <label
                    htmlFor="projectType"
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Project Type *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    required
                    value={formData.projectType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#CCCCCC] text-[#1A1A1A] focus:outline-none focus:border-[#FF8000] transition-colors duration-300 rounded-lg"
                  >
                    <option value="">Select project type</option>
                    <option value="renovation">Home Renovation</option>
                    <option value="new-home">New Home Design</option>
                    <option value="single-room">Single Room Redesign</option>
                    <option value="commercial">Commercial Space</option>
                    <option value="consultation">Design Consultation</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Tell Us About Your Project
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#CCCCCC] text-[#1A1A1A] focus:outline-none focus:border-[#FF8000] transition-colors duration-300 resize-none rounded-lg"
                    placeholder="Share your vision, timeline, and any specific requirements..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-interest-submit"
                  className="w-full bg-[#FF8000] text-white border-none rounded-2xl px-8 py-4 text-lg font-medium cursor-pointer transition-all duration-400 hover:bg-[#FF6B00] hover:shadow-lg min-h-[56px] flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Register Your Interest
                      <Send size={20} />
                    </>
                  )}
                </button>
                {error && (
                  <p data-testid="register-interest-error" className="text-red-500 text-sm text-center mt-2">{error}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;